import { Cloud, Plus, Search, Server } from "lucide-react";
import { ServerRow } from "./ServerRow";
import { ServerCapNote } from "./ServerCapNote";
import type { AutomationServer } from "@/data/automation-server";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import React, { useMemo, useEffect } from "react";
import { useAutomationServersQuery } from "@/hooks/useAutomationServersQuery";
import { Skeleton } from "../ui/skeleton";

export type NewServerKind = "hetzner" | "byos";

type AutomationServerListSectionProps = {
  highlightedServerId: string | null;
  onNewServer: (kind: NewServerKind) => void;
  orgId?: string;
  orgName?: string;
  onOpenDetails?: (server: AutomationServer) => void;
};

// A server is "Bailey" when it self-reported a bailey_url at registration
// (see issue #63); otherwise it's a legacy server.

export function AutomationServerListSection(
  props: AutomationServerListSectionProps,
) {
  const { highlightedServerId, onNewServer, orgId, orgName, onOpenDetails } = props;
  // Search state with debouncing
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");

  // Debounce search input to avoid too many requests while typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const { data, hasNextPage, fetchNextPage, isFetching, isFetchingNextPage } = useAutomationServersQuery(debouncedSearch, orgId);
  
  // Flatten all pages into a single array
  const servers = useMemo(
    () => data?.pages.flatMap((page) => page.results) ?? [],
    [data],
  );

  const loadMoreRef = React.useRef<HTMLLIElement>(null);

  React.useEffect(() => {
    const observerElement = loadMoreRef.current;
    if (!observerElement || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        rootMargin: "100px", // Start loading slightly before the element is visible
      }
    );

    observer.observe(observerElement);

    return () => {
      if (observerElement) {
        observer.unobserve(observerElement);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="mx-auto flex-1 px-0 py-4">
      <div className="mb-4 flex items-center justify-between">
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search servers..."
            className="bg-white pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <ul className="divide-y overflow-hidden rounded-xl border bg-card" data-testid="server-list">
        {servers.map((server) => (
          <ServerRow key={server.id} server={server} highlighted={highlightedServerId === server.automation_server_id} onOpenDetails={onOpenDetails} />
        ))}
        {(hasNextPage || isFetching) && (
          <li ref={loadMoreRef} className="flex items-center gap-5 px-5 py-4">
            <Skeleton className="h-11 w-11 rounded-xl" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
            <Skeleton className="h-8 w-40" />
          </li>
        )}
        {!isFetching && servers.length === 0 && debouncedSearch && (
          <li className="px-5 py-8 text-center text-sm text-muted-foreground">No servers match "{debouncedSearch}".</li>
        )}
        <NewServerRow first={!isFetching && servers.length === 0 && !debouncedSearch} onNewServer={onNewServer} />
      </ul>
      {!isFetching && (
        <div className="mt-4">
          <ServerCapNote orgId={orgId} orgName={orgName} cloudCount={servers.filter((server) => server.server_type === "hetzner").length} />
        </div>
      )}
    </div>
  );
}

function NewServerRow({ first, onNewServer }: { first: boolean; onNewServer: (kind: NewServerKind) => void }) {
  return (
    <li className="flex flex-wrap items-center gap-x-5 gap-y-3 bg-muted/20 px-5 py-4" data-testid="new-server-row">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
        <Plus className="h-5 w-5" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1 basis-56">
        <p className="text-base font-semibold leading-tight">{first ? "Add your first server" : "Add another server"}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">
          A Bailey server runs your automations. BitSwan can host one for you, or you connect a machine of your own.
        </p>
      </div>
      <div className="flex items-center rounded-lg border bg-background" data-testid="new-server-options">
        <Button variant="ghost" size="sm" className="rounded-r-none" onClick={() => onNewServer("hetzner")} data-testid="new-server-cloud">
          <Cloud className="mr-1.5 h-3.5 w-3.5 text-sky-700" />
          Private cloud server
        </Button>
        <span className="h-6 w-px bg-border" />
        <Button variant="ghost" size="sm" className="rounded-l-none" onClick={() => onNewServer("byos")} data-testid="new-server-byos">
          <Server className="mr-1.5 h-3.5 w-3.5 text-slate-700" />
          Bring your own
        </Button>
      </div>
    </li>
  );
}
